import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { UserButton } from "@clerk/clerk-react";
import { Menu, X, Volume2, VolumeX, RotateCcw, Crown } from 'lucide-react';
import { motion } from 'framer-motion';

const Navbar = () => {
  const { balance, resetGame, isMuted, toggleMute } = useGame();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path) =>
    location.pathname === path
      ? 'text-yellow-300 border-b-2 border-yellow-300 glow-text'
      : 'text-white/70 hover:text-cyan-300';

  return (
    <nav className="w-full h-[64px] sticky top-0 z-[100] backdrop-blur-xl bg-gradient-to-r from-black/60 via-black/40 to-black/60 border-b border-cyan-400/20 shadow-[0_0_40px_rgba(34,211,238,0.15)]">

      {/* LED Strip */}
      <div className="absolute top-0 left-5 right-0 h-[2px] bg-gradient-to-r from-yellow-300 via-fuchsia-400 to-cyan-300 animate-pulse" />

      <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-6">

        {/* BRAND */}
        <Link to="/" className="flex items-center gap-4 group">
          <div className="relative">
            <div className="absolute inset-0 bg-fuchsia-500 blur-xl opacity-30 group-hover:opacity-60" />
            <div className="relative bg-gradient-to-br from-yellow-300 via-fuchsia-400 to-cyan-400 p-2.5 rounded-xl shadow-[0_0_25px_rgba(236,72,153,0.45)] border border-white/20">
              <Crown className="text-black w-6 h-6" />
            </div>
          </div>
          <div className="leading-tight">
            <span className="text-[10px] tracking-[0.35em] uppercase text-cyan-200 font-black">DataSense</span>
            <div className="text-xl font-black tracking-widest bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 via-fuchsia-300 to-cyan-300">
              Casino Royale
            </div>
          </div>
        </Link>

        {/* CENTER LINKS */}
        <div className="hidden md:flex items-center gap-10 text-[11px] font-black tracking-[0.25em] uppercase">
          <Link to="/game" className={isActive('/game')}>Casino</Link>
          <Link to="/map" className={isActive('/map')}>Map</Link>
          <Link to="/missions" className={isActive('/missions')}>Acheivements</Link>
          <Link to="/challenges" className={isActive('/challenges')}>Dailies</Link>
          <Link to="/flipcards" className={isActive('/flipcards')}>High Roller</Link>
          <Link to="/instructions" className={isActive('/instructions')}>Guide</Link>
        </div>

        {/* RIGHT HUD */}
        <div className="flex items-center gap-6">

          <div className="flex items-center gap-4 text-white/70">
            <button onClick={toggleMute} className="hover:text-cyan-300 transition">
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <button onClick={resetGame} className="hover:text-red-400 transition" title="Factory Reset">
              <RotateCcw size={18} />
            </button>
          </div>

          <div className="h-8 w-px bg-white/10" />

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-[9px] tracking-widest uppercase text-yellow-200/70">Chips</span>
              <span className="text-lg font-mono font-black text-yellow-200 glow-gold">
                ${(balance || 0).toLocaleString()}
              </span>
            </div>

            {/* <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-cyan-300 to-fuchsia-400 border border-white/30 flex items-center justify-center shadow-[0_0_25px_rgba(34,211,238,0.4)]">
              <span className="font-black text-xs text-black">{user?.level || 1}</span>
            </div> */}
          </div>

          {/* CLERK USER BUTTON */}
          <div className="flex items-center gap-3">
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "w-9 h-9 ring-2 ring-cyan-400/50 hover:ring-cyan-400 transition-all"
                }
              }}
            />
          </div>
        </div>

        {/* MOBILE */}
        <button className="md:hidden text-cyan-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {/* MOBILE PANEL */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-[64px] left-0 w-full z-[100] bg-[#050505]/95 backdrop-blur-2xl border-b border-cyan-400/20 p-6 flex flex-col gap-3 md:hidden shadow-2xl"
        >
          {[
            ['Casino', '/game'],
            ['Missions', '/missions'],
            ['Dailies', '/challenges'],
            ['High Roller', '/flipcards'],
            ['Guide', '/instructions']
          ].map(([label, path]) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`p-4 rounded-xl border font-black tracking-widest uppercase text-xs transition-all duration-300 ${location.pathname === path
                  ? 'bg-gradient-to-r from-yellow-900/40 via-fuchsia-900/40 to-cyan-900/40 border-cyan-400/50 text-cyan-200 shadow-[0_0_15px_rgba(34,211,238,0.2)]'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-cyan-400/30 hover:text-cyan-200'
                }`}
            >
              <div className="flex items-center justify-between">
                <span>{label}</span>
                {location.pathname === path && (
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-pulse" />
                )}
              </div>
            </Link>
          ))}
        </motion.div>
      )
      }
    </nav >
  );
};

export default Navbar;
